import { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { useReplaceSaving } from 'contexts/ReplaceSavingContext';
import { httpGetPool } from 'services/pools';
import { httpUpdatePump } from 'services/pumps';
import {
  PUMP_STATUS_COLOR_CLASS,
  PUMP_STATUS_MENU_ORDER,
  pumpStatusTranslationKey,
} from './pumpStatusMeta';
import './PumpsMainView.scss';
import './ReplacePumpInPoolView.scss';

const STATUS_IN_USE = 2;
const STATUS_REMOVED_FROM_POOL = 8;

const STATUS_FILTER_VALUES = [null, ...PUMP_STATUS_MENU_ORDER];

export default function ReplacePumpInPoolView() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fi') ? 'fi-FI' : 'en-GB';
  const { poolId, currentPumpId } = useParams();
  const navigate = useNavigate();
  const { setReplaceSavingInProgress } = useReplaceSaving() || {};

  const pumpsFromStore = useStoreState((state) => state.pumps.pumps);
  const pumpsLoading = useStoreState((state) => state.pumps.loading);
  const getPumps = useStoreActions((actions) => actions.pumps.getPumps);
  const poolsFromStore = useStoreState((state) => state.pools.pools);
  const getPools = useStoreActions((actions) => actions.pools.getPools);

  const [poolLoading, setPoolLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingPumpId, setSavingPumpId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [selectedPumpId, setSelectedPumpId] = useState(null);
  const [previousPumpStatus, setPreviousPumpStatus] = useState(STATUS_REMOVED_FROM_POOL);
  const [poolFilterOpen, setPoolFilterOpen] = useState(false);
  const [previousStatusDropdownOpen, setPreviousStatusDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPoolId, setFilterPoolId] = useState(null);

  const statusFilterOptions = useMemo(
    () =>
      STATUS_FILTER_VALUES.map((value) => ({
        value,
        label:
          value == null ? t('replace_pump_all_statuses') : t(pumpStatusTranslationKey(value)),
      })),
    [t]
  );

  const previousStatusBaseOptions = useMemo(
    () =>
      PUMP_STATUS_MENU_ORDER.filter((v) => v !== STATUS_IN_USE).map((value) => ({
        value,
        label: t(pumpStatusTranslationKey(value)),
      })),
    [t]
  );

  const poolIdNum = poolId ? parseInt(poolId, 10) : null;
  const currentPumpIdNum = currentPumpId ? parseInt(currentPumpId, 10) : null;

  const poolFromStore = useMemo(
    () => (poolIdNum && poolsFromStore?.length ? poolsFromStore.find((p) => p.id === poolIdNum) : null),
    [poolIdNum, poolsFromStore]
  );

  const availablePumps = useMemo(() => {
    const list = Array.isArray(pumpsFromStore) ? pumpsFromStore : [];
    let filtered = list.filter((p) => {
      if (p.id === currentPumpIdNum) return false;
      if (selectedStatus != null) return p.status === selectedStatus;
      return true;
    });
    if (filterPoolId != null) {
      filtered = filtered.filter((p) => {
        const pumpPoolId = p.current_pool?.id ?? p.current_pool;
        return pumpPoolId === filterPoolId;
      });
    }
    const term = (searchTerm || '').toLowerCase().trim();
    if (!term) return filtered;
    return filtered.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(term)) ||
        (p.serial_number && p.serial_number.toLowerCase().includes(term)) ||
        (p.model && p.model.toLowerCase().includes(term))
    );
  }, [pumpsFromStore, currentPumpIdNum, selectedStatus, searchTerm, filterPoolId]);

  useEffect(() => {
    if (!poolIdNum) return;
    if (poolFromStore) {
      return;
    }
    setPoolLoading(true);
    setError(null);
    httpGetPool(poolId)
      .then(() => {})
      .catch((err) => {
        console.error('ReplacePumpInPoolView pool fetch error:', err);
        setError(t('replace_pump_err_pool'));
      })
      .finally(() => setPoolLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only for error message
  }, [poolIdNum, poolFromStore, poolId]);

  useEffect(() => {
    if (poolsFromStore.length === 0) {
      getPools().catch(() => {});
    }
  }, [poolsFromStore.length, getPools]);

  useEffect(() => {
    if (!poolId || !currentPumpId) return;
    if (pumpsFromStore.length > 0 || pumpsLoading) return;
    getPumps({}).catch((err) => {
      console.error('ReplacePumpInPoolView getPumps error:', err);
      setError(t('replace_pump_err_list'));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only for error message
  }, [poolId, currentPumpId, pumpsFromStore.length, pumpsLoading, getPumps]);

  const handleBack = () => {
    navigate(`/dashboard/pools/${poolId}`);
  };

  const handlePoolFilterSelect = (selectedPool) => {
    setPoolFilterOpen(false);
    setFilterPoolId(selectedPool ? selectedPool.id : null);
  };

  const filterPoolLabel =
    filterPoolId == null
      ? t('replace_pump_all_pools')
      : poolsFromStore?.find((p) => p.id === filterPoolId)?.name ?? t('ui_loading');

  const formatLastService = (pumpRow) => {
    if (pumpRow.last_maintenance_date) {
      const date = new Date(pumpRow.last_maintenance_date);
      return t('replace_pump_last_service', { date: date.toLocaleDateString(dateLocale) });
    }
    return pumpRow.formatted_active_time || '—';
  };

  const statusBadgeLabel = (code) => {
    const key = pumpStatusTranslationKey(code);
    if (key === 'pump_status_unknown') {
      return t('replace_pump_status_fallback', { code });
    }
    return t(key);
  };

  const handleSaveChange = async () => {
    if (!selectedPumpId || !poolId || !currentPumpId) return;
    const newPump = availablePumps.find((p) => p.id === selectedPumpId);
    if (!newPump) return;
    setSaving(true);
    setSavingPumpId(newPump.id);
    setError(null);
    if (setReplaceSavingInProgress) setReplaceSavingInProgress(true);
    try {
      await httpUpdatePump(parseInt(currentPumpId, 10), {
        status: previousPumpStatus,
        current_pool_id: null,
      });
      const updatedRes = await httpUpdatePump(newPump.id, {
        status: STATUS_IN_USE,
        current_pool_id: parseInt(poolId, 10),
      });
      const newPumpData = updatedRes.data;
      navigate(`/dashboard/pools/${poolId}`, {
        replace: true,
        state: {
          replaceSaving: true,
          replaceResult: {
            replacedPumpId: parseInt(currentPumpId, 10),
            newPump: newPumpData,
          },
        },
      });
    } catch (err) {
      console.error('Replace pump error:', err);
      setError(err?.response?.data?.detail || err?.message || t('replace_pump_err_swap'));
      setSaving(false);
      setSavingPumpId(null);
    }
  };

  const poolsArray = Array.isArray(poolsFromStore) ? poolsFromStore : [];

  if (!poolId || !currentPumpId) {
    return (
      <div className="replace-pump-view">
        <div className="replace-pump-header">
          <button type="button" className="back-btn" onClick={handleBack}>
            ←
          </button>
          <h1 className="replace-pump-title">{t('replace_pump_title')}</h1>
        </div>
        <p className="text-muted">{t('replace_pump_missing')}</p>
      </div>
    );
  }

  return (
    <div className="replace-pump-view">
      <div className="replace-pump-header">
        <button
          type="button"
          className="back-btn"
          onClick={handleBack}
          aria-label={t('replace_pump_back_aria')}
        >
          <i className="fas fa-arrow-left" />
        </button>
        <h1 className="replace-pump-title">{t('replace_pump_select_title')}</h1>
      </div>

      <div className="replace-pump-toolbar dashboard-toolbar d-flex align-items-center flex-nowrap justify-content-between gap-2">
        <div className="replace-pump-toolbar-start d-flex align-items-center flex-nowrap gap-2">
          <div className="pool-selector d-flex align-items-center flex-shrink-0">
            <i className="fas fa-map-marker-alt mr-2" aria-hidden />
            <Dropdown isOpen={poolFilterOpen} toggle={() => setPoolFilterOpen(!poolFilterOpen)} className="pool-dropdown">
              <DropdownToggle caret color="link" className="p-0 text-dark">
                {filterPoolLabel}
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => handlePoolFilterSelect(null)} active={filterPoolId == null}>
                  {t('replace_pump_all_pools')}
                </DropdownItem>
                {poolsArray.map((p) => (
                  <DropdownItem
                    key={p.id}
                    onClick={() => handlePoolFilterSelect(p)}
                    active={filterPoolId === p.id}
                  >
                    {p.name}
                  </DropdownItem>
                ))}
                {poolsArray.length === 0 && !poolLoading && (
                  <DropdownItem disabled>{t('replace_pump_no_pools')}</DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          </div>
          <div className="status-selector d-flex align-items-center flex-shrink-0">
            <i className="fas fa-filter mr-2" style={{ color: '#666' }} />
            <Dropdown isOpen={statusDropdownOpen} toggle={() => setStatusDropdownOpen(!statusDropdownOpen)}>
              <DropdownToggle caret color="link" className="p-0 text-dark">
                {selectedStatus != null
                  ? statusFilterOptions.find((s) => s.value === selectedStatus)?.label
                  : t('replace_pump_all_statuses')}
              </DropdownToggle>
              <DropdownMenu container="body" className="pump-status-filter-menu">
                {statusFilterOptions.map((option) => (
                  <DropdownItem
                    key={option.value ?? 'all'}
                    onClick={() => setSelectedStatus(option.value)}
                    active={selectedStatus === option.value}
                  >
                    {option.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
        <div className="pump-search-wrapper toolbar-search-end">
          <i className="fas fa-search pump-search-icon" />
          <input
            type="text"
            className="pump-search-input"
            placeholder={t('replace_pump_search_ph')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="pump-search-clear"
              onClick={() => setSearchTerm('')}
              aria-label={t('replace_pump_clear_search')}
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="replace-pump-content">
        {error && <div className="replace-pump-error">{error}</div>}

        {pumpsLoading && pumpsFromStore.length === 0 ? (
          <div className="text-center py-5">{t('replace_pump_loading')}</div>
        ) : availablePumps.length === 0 ? (
          <div className="replace-pump-empty">
            {filterPoolId != null
              ? t('replace_pump_empty_pool')
              : selectedStatus != null
                ? t('replace_pump_empty_status')
                : t('replace_pump_empty_none')}
          </div>
        ) : (
          <div className="replace-pump-list">
            {availablePumps.map((pumpRow) => {
              const poolName = pumpRow.current_pool?.name || t('replace_pump_no_pool');
              const lastServiceText = formatLastService(pumpRow);
              const isSelected = selectedPumpId === pumpRow.id;
              const daysMatch =
                pumpRow.formatted_active_time && String(pumpRow.formatted_active_time).match(/^(\d+)vrk/);
              const isOverdue = daysMatch && parseInt(daysMatch[1], 10) > 90;
              return (
                <div
                  key={pumpRow.id}
                  className={`replace-pump-item ${isSelected ? 'replace-pump-item--selected' : ''}`}
                  onClick={() => !saving && setSelectedPumpId(isSelected ? null : pumpRow.id)}
                  aria-pressed={isSelected}
                  aria-busy={savingPumpId === pumpRow.id}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (!saving) setSelectedPumpId(isSelected ? null : pumpRow.id);
                    }
                  }}
                  style={saving ? { pointerEvents: 'none', opacity: 0.8 } : undefined}
                >
                  <div className="replace-pump-item-main">
                    <div className="replace-pump-item-name">{pumpRow.name}</div>
                    <div className="replace-pump-item-details">
                      {(pumpRow.unacknowledged_comments_count || 0) > 0 && (
                        <span className="replace-pump-comment-badge">
                          <i className="far fa-comment-alt" />
                          <span>{pumpRow.unacknowledged_comments_count}</span>
                        </span>
                      )}
                      <span>{poolName}</span>
                      <span className="replace-pump-details-sep">·</span>
                      <span
                        className={`replace-pump-last-service ${isOverdue ? 'replace-pump-last-service--overdue' : ''}`}
                      >
                        {lastServiceText}
                      </span>
                    </div>
                  </div>
                  <div className="replace-pump-item-meta">
                    <span
                      className={`replace-pump-status-badge ${PUMP_STATUS_COLOR_CLASS[pumpRow.status] || 'status-orange'}`}
                    >
                      {statusBadgeLabel(pumpRow.status)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedPumpId && (
        <div className="replace-pump-action-bar">
          <p className="replace-pump-action-label">{t('replace_pump_move_prev')}</p>
          <Dropdown
            isOpen={previousStatusDropdownOpen}
            toggle={() => setPreviousStatusDropdownOpen(!previousStatusDropdownOpen)}
            className="replace-pump-previous-status-dropdown"
          >
            <DropdownToggle
              tag="div"
              className="replace-pump-previous-status-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setPreviousStatusDropdownOpen(!previousStatusDropdownOpen);
              }}
            >
              {previousStatusBaseOptions.find((s) => s.value === previousPumpStatus)?.label ??
                t(pumpStatusTranslationKey(STATUS_REMOVED_FROM_POOL))}
              <i className="fas fa-chevron-down" />
            </DropdownToggle>
            <DropdownMenu>
              {previousStatusBaseOptions.map((option) => (
                <DropdownItem
                  key={option.value}
                  onClick={() => {
                    setPreviousPumpStatus(option.value);
                    setPreviousStatusDropdownOpen(false);
                  }}
                  active={previousPumpStatus === option.value}
                >
                  {option.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <button type="button" className="replace-pump-save-btn" onClick={handleSaveChange} disabled={saving}>
            {saving ? t('ui_saving') : t('replace_pump_save_commit')}
          </button>
        </div>
      )}
    </div>
  );
}
